import { formatDate, isDateLike } from "../../helpers/utils";
import { checkResponse } from ".";
import { buildUrl, fetchWithAuth, safeJson } from "./api";

export interface PagingParams {
  size: number;
  page: number;
  filter?: ColumnFiltersState;
  sort?: SortingState;
}

interface CountParams {
  filter?: ColumnFiltersState;
}

export type FilterObj = Record<string, string>;

export type FilterObjBuilder = (filter?: ColumnFiltersState) => FilterObj;

export interface ColumnSort {
  desc: boolean;
  id: string;
}

export type SortingState = ColumnSort[];

export type ColumnFiltersState = ColumnFilter[];

export interface ColumnFilter {
  id: string;
  operator: string;
  value: unknown;
}

const formatFilterValue = (value: unknown): string => {
  if (typeof value === "string") {
    if (!Number.isNaN(Number(value))) {
      return String(value).trim();
    }

    if (isDateLike(value)) {
      return formatDate(value);
    }

    return `"${value}"`;
  }

  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === "string" && isDateLike(v))) {
      return value.map(formatDate).join(",");
    }

    return value.join(",");
  }

  return String(value).trim();
};

const serializeFilter = (f: ColumnFilter) => {
  switch (f.operator) {
    case "blank":
      return `${f.id} isNull`;
    case "notBlank":
      return `${f.id} isNotNull`;
    case "contains":
      return `${f.id} contains ${formatFilterValue(f.value)}`;
    case "notContains":
      return `${f.id} contains ${formatFilterValue(f.value)}`;
    case "equals":
      return `${f.id} equals ${formatFilterValue(f.value)}`;
    case "notEqual":
      return `${f.id} notEqual ${formatFilterValue(f.value)}`;
    case "greaterThan":
      return `${f.id} greaterThan ${formatFilterValue(f.value)}`;
    case "greaterThanOrEqual":
      return `${f.id} greaterThanOrEqual ${formatFilterValue(f.value)}`;
    case "lessThan":
      return `${f.id} lessThan ${formatFilterValue(f.value)}`;
    case "lessThanOrEqual":
      return `${f.id} lessThanOrEqual ${formatFilterValue(f.value)}`;
    case "inRange":
      if (!Array.isArray(f.value) || f.value.length !== 2) {
        return null;
      }
      return `${f.id} inRange ${formatFilterValue(f.value)}`;
    case "startsWith":
      return `${f.id} startsWith ${formatFilterValue(f.value)}`;
    case "endsWith":
      return `${f.id} endsWith ${formatFilterValue(f.value)}`;
    case "true":
      return `${f.id} equals true`;
    case "false":
      return `${f.id} equals false`;
    default:
      throw new Error(`Unsupported operator ${f.operator}`);
  }
};

export const columnFiltersToBackendString = (
  filters?: ColumnFiltersState,
): string | undefined => {
  if (!filters || filters.length === 0) return undefined;

  const parts = filters
    .map((f) => {
      if (!f.value) return null;
      return serializeFilter(f);
    })
    .filter(Boolean);

  return parts.length ? parts.join(";") : undefined;
};

export const sortingStateToBackendParams = (
  sortingState?: SortingState,
): Record<string, string> => {
  if (!sortingState || sortingState.length === 0) {
    return {};
  }

  const { id, desc } = sortingState[0];

  return {
    sort: `${id}:${desc ? "desc" : "asc"}`,
  };
};

const defaultBuildFilterObj: FilterObjBuilder = (filter) => {
  const serializedFilter = columnFiltersToBackendString(filter);

  return serializedFilter ? { filter: serializedFilter } : {};
};

/**
 * Creates a pager object with getPage and getCount methods for fetching paginated data from the backend.
 * That means that data will not be stored in redux store, but will be fetched on demand when the user navigates through pages or applies filters/sorting.
 * @param basePath The base path for the API endpoints (i.e. "/api/v1/users").
 * @param buildFilterObj Function used to transform the filter state into query params.
 * @returns An object with getPage and getCount methods.
 */
export const createPager = (
  basePath: string,
  buildFilterObj: FilterObjBuilder = defaultBuildFilterObj,
) => {
  const getPage = async ({ size, page, filter, sort }: PagingParams) => {
    const filterObj = buildFilterObj(filter);
    const sortObj = sortingStateToBackendParams(sort);
    const url = buildUrl(basePath, {
      pageSize: size,
      page,
      ...filterObj,
      ...sortObj,
    });
    const res = await fetchWithAuth(url);
    return safeJson(res);
  };

  const getCount = async ({ filter }: CountParams) => {
    const filterObj = buildFilterObj(filter);
    const url = buildUrl(`${basePath}/count`, filterObj);
    const res = await fetchWithAuth(url);
    const json = await safeJson(res);
    return checkResponse(json, `${basePath} count`);
  };

  return {
    getPage,
    getCount,
  };
};
